import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Loader2, ExternalLink } from 'lucide-react';
import { Input } from './ui/input';

/**
 * Adress-Eingabe mit Vorschlaegen von OpenStreetMap (Nominatim).
 * Kostenlos, ohne API-Key. Nutzungsbedingungen: max. 1 Anfrage/Sekunde,
 * deshalb wird die Eingabe entprellt (Debounce) und erst ab 3 Zeichen gesucht.
 *
 * Props: value, onChange(string), placeholder, className, countryCodes (Standard: at,de,ch)
 */
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';

/** Google-Maps-Suchlink fuer eine freie Adresse */
export const mapsUrl = (address) =>
    `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address || '')}`;

/** Klickbare Adresse, oeffnet Google Maps in neuem Tab */
export const AddressLink = ({ address, className = '', iconClassName = 'w-3 h-3', children }) => {
    if (!address) return null;
    return (
        <a
            href={mapsUrl(address)}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            title="In Google Maps öffnen"
            className={`inline-flex items-center gap-1 hover:underline underline-offset-2 ${className}`}
            data-testid="address-link"
        >
            <MapPin className={`${iconClassName} flex-shrink-0`} />
            {children || <span className="truncate">{address}</span>}
        </a>
    );
};

const AddressInput = ({
    value,
    onChange,
    placeholder = 'Adresse',
    className = '',
    countryCodes = 'at,de,ch',
    ...rest
}) => {
    const [suggestions, setSuggestions] = useState([]);
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [activeIndex, setActiveIndex] = useState(-1);
    const skipNextSearch = useRef(false);
    const abortRef = useRef(null);
    const wrapperRef = useRef(null);

    // Suche mit Debounce (400ms), ab 3 Zeichen
    useEffect(() => {
        if (skipNextSearch.current) {
            skipNextSearch.current = false;
            return;
        }
        const q = (value || '').trim();
        if (q.length < 3) {
            setSuggestions([]);
            setLoading(false);
            return;
        }
        const timer = setTimeout(async () => {
            if (abortRef.current) abortRef.current.abort();
            const controller = new AbortController();
            abortRef.current = controller;
            setLoading(true);
            try {
                const params = new URLSearchParams({
                    q,
                    format: 'jsonv2',
                    addressdetails: '1',
                    limit: '6',
                    'accept-language': 'de',
                });
                if (countryCodes) params.set('countrycodes', countryCodes);
                const res = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
                    signal: controller.signal,
                    headers: { Accept: 'application/json' },
                });
                if (!res.ok) throw new Error('Nominatim-Fehler');
                const data = await res.json();
                setSuggestions(Array.isArray(data) ? data.map(formatSuggestion) : []);
                setOpen(true);
                setActiveIndex(-1);
            } catch (e) {
                if (e.name !== 'AbortError') setSuggestions([]);
            } finally {
                setLoading(false);
            }
        }, 400);
        return () => clearTimeout(timer);
    }, [value, countryCodes]);

    // Klick ausserhalb schliesst die Liste
    useEffect(() => {
        const onDocClick = (e) => {
            if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener('mousedown', onDocClick);
        return () => document.removeEventListener('mousedown', onDocClick);
    }, []);

    const pick = (s) => {
        skipNextSearch.current = true;
        onChange(s.label);
        setSuggestions([]);
        setOpen(false);
    };

    const onKeyDown = (e) => {
        if (!open || suggestions.length === 0) return;
        if (e.key === 'ArrowDown') {
            e.preventDefault();
            setActiveIndex(i => (i + 1) % suggestions.length);
        } else if (e.key === 'ArrowUp') {
            e.preventDefault();
            setActiveIndex(i => (i <= 0 ? suggestions.length - 1 : i - 1));
        } else if (e.key === 'Enter' && activeIndex >= 0) {
            e.preventDefault();
            pick(suggestions[activeIndex]);
        } else if (e.key === 'Escape') {
            setOpen(false);
        }
    };

    return (
        <div ref={wrapperRef} className="relative" data-testid="address-input">
            <div className="relative">
                <Input
                    value={value || ''}
                    onChange={(e) => onChange(e.target.value)}
                    onFocus={() => suggestions.length > 0 && setOpen(true)}
                    onKeyDown={onKeyDown}
                    placeholder={placeholder}
                    className={`pr-16 ${className}`}
                    autoComplete="off"
                    {...rest}
                />
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                    {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />}
                    {value && (
                        <a
                            href={mapsUrl(value)}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="In Google Maps öffnen"
                            className="text-muted-foreground hover:text-primary transition-colors"
                            tabIndex={-1}
                            data-testid="address-input-maps-link"
                        >
                            <ExternalLink className="w-3.5 h-3.5" />
                        </a>
                    )}
                </div>
            </div>

            {open && suggestions.length > 0 && (
                <ul
                    className="mt-1 max-h-56 overflow-y-auto rounded-lg border border-border bg-popover text-popover-foreground shadow-md text-sm"
                    role="listbox"
                    data-testid="address-suggestions"
                >
                    {suggestions.map((s, i) => (
                        <li
                            key={s.id}
                            role="option"
                            aria-selected={i === activeIndex}
                            onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                            onMouseEnter={() => setActiveIndex(i)}
                            className={`flex items-start gap-2 px-3 py-2 cursor-pointer ${i === activeIndex ? 'bg-muted' : 'hover:bg-muted/60'}`}
                        >
                            <MapPin className="w-3.5 h-3.5 mt-0.5 text-muted-foreground flex-shrink-0" />
                            <div className="min-w-0">
                                <p className="truncate font-medium">{s.primary}</p>
                                {s.secondary && <p className="truncate text-xs text-muted-foreground">{s.secondary}</p>}
                            </div>
                        </li>
                    ))}
                    <li className="px-3 py-1 text-[10px] text-muted-foreground border-t border-border/50">
                        Vorschläge von OpenStreetMap
                    </li>
                </ul>
            )}
        </div>
    );
};

/** Nominatim-Treffer in eine kompakte, gut lesbare Adresse umwandeln */
function formatSuggestion(item) {
    const a = item.address || {};
    const street = [a.road || a.pedestrian || a.footway || a.path, a.house_number].filter(Boolean).join(' ');
    const city = a.city || a.town || a.village || a.municipality || a.suburb || a.county || '';
    const postcode = a.postcode || '';
    const country = a.country || '';
    const name = item.name && item.name !== street ? item.name : '';

    const primary = name || street || item.display_name.split(',')[0];
    const secondaryParts = [];
    if (name && street) secondaryParts.push(street);
    if (postcode || city) secondaryParts.push([postcode, city].filter(Boolean).join(' '));
    if (country) secondaryParts.push(country);
    const secondary = secondaryParts.join(', ');

    // Wert, der ins Feld uebernommen wird: "Name, Strasse Nr, PLZ Ort" (ohne Land, wenn Oesterreich)
    const labelParts = [];
    if (name) labelParts.push(name);
    if (street) labelParts.push(street);
    if (postcode || city) labelParts.push([postcode, city].filter(Boolean).join(' '));
    if (country && a.country_code !== 'at') labelParts.push(country);
    const label = labelParts.length > 0 ? labelParts.join(', ') : item.display_name;

    return { id: `${item.place_id}`, primary, secondary, label };
}

export default AddressInput;
